import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronDown, ChevronRight, Filter, GripVertical } from 'lucide-react';
import type { ColumnMode } from '../../lib/subscriberListColumns';

export type SubscriberListFieldChip = {
  key: string;
  label: string;
  title?: string;
  mode: ColumnMode;
};

type SubscriberListFieldsPanelProps = {
  fields: SubscriberListFieldChip[];
  displayedCount: number;
  enforcedCount: number;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onReorder: (keys: string[]) => void;
  onCycleMode: (key: string) => void;
  onReset: () => void;
  showReset: boolean;
};

function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function modeChipClasses(mode: ColumnMode, isDragging: boolean): string {
  const base = `inline-flex items-stretch rounded-full border text-xs font-medium transition-colors ${
    isDragging ? 'opacity-50' : ''
  }`;
  if (mode === 'require_value') {
    return `${base} border-amber-500 bg-emerald-500 text-white ring-2 ring-amber-400/80 ring-offset-1 ring-offset-white dark:ring-offset-gray-800`;
  }
  if (mode === 'show') {
    return `${base} border-emerald-500 bg-emerald-500 text-white`;
  }
  return `${base} border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200`;
}

function modeButtonHover(mode: ColumnMode): string {
  if (mode === 'hidden') return 'hover:bg-gray-50 dark:hover:bg-gray-600';
  return 'hover:bg-emerald-600';
}

function modeTitle(mode: ColumnMode, label: string): string {
  if (mode === 'require_value') {
    return `${label}: visible column — only rows with a value (click to hide)`;
  }
  if (mode === 'show') {
    return `${label}: visible column (click to require a value)`;
  }
  return `${label}: hidden (click to show column)`;
}

/** Nearest column chip to the pointer, with a little slack so the drop does not have to be exact. */
function chipKeyAtPoint(x: number, y: number, root: HTMLElement | null): string | null {
  if (!root) return null;
  let bestKey: string | null = null;
  let best = Infinity;
  for (const el of root.querySelectorAll<HTMLElement>('[data-field-key]')) {
    const rect = el.getBoundingClientRect();
    const cx = Math.max(rect.left, Math.min(x, rect.right));
    const cy = Math.max(rect.top, Math.min(y, rect.bottom));
    const dist = Math.hypot(x - cx, y - cy);
    if (dist < best) {
      best = dist;
      bestKey = el.getAttribute('data-field-key');
    }
  }
  return best != null && best <= 48 ? bestKey : null;
}

function pointerPassedChipMidpoint(
  event: PointerEvent,
  from: number,
  to: number,
  chip: HTMLElement,
): boolean {
  const rect = chip.getBoundingClientRect();
  const movingForward = to > from;
  const sameRow = event.clientY >= rect.top && event.clientY <= rect.bottom;
  if (sameRow) {
    const midX = rect.left + rect.width / 2;
    return movingForward ? event.clientX >= midX : event.clientX <= midX;
  }
  const midY = rect.top + rect.height / 2;
  return movingForward ? event.clientY >= midY : event.clientY <= midY;
}

export default function SubscriberListFieldsPanel({
  fields,
  displayedCount,
  enforcedCount,
  collapsed,
  onCollapsedChange,
  onReorder,
  onCycleMode,
  onReset,
  showReset,
}: SubscriberListFieldsPanelProps) {
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;

  const suppressClickRef = useRef(false);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => dragCleanupRef.current?.(), []);

  const onChipPointerDown = (key: string, event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    dragCleanupRef.current?.();
    const startX = event.clientX;
    const startY = event.clientY;
    let dragging = false;
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;

    const move = (ev: PointerEvent) => {
      if (!dragging) {
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < 6) return;
        dragging = true;
        suppressClickRef.current = true;
        setDragKey(key);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
      }
      const toKey = chipKeyAtPoint(ev.clientX, ev.clientY, listRef.current);
      setDropKey((prev) => (prev === toKey ? prev : toKey));
      if (!toKey || toKey === key || !listRef.current) return;
      const keys = fieldsRef.current.map((field) => field.key);
      const from = keys.indexOf(key);
      const to = keys.indexOf(toKey);
      const chip = listRef.current.querySelector<HTMLElement>(
        `[data-field-key="${CSS.escape(toKey)}"]`,
      );
      if (from < 0 || to < 0 || !chip) return;
      if (!pointerPassedChipMidpoint(ev, from, to, chip)) return;
      onReorderRef.current(moveItem(keys, from, to));
    };

    const end = () => {
      dragCleanupRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      setDragKey(null);
      setDropKey(null);
      if (dragging) {
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 50);
      }
    };
    dragCleanupRef.current = end;

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  return (
    <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => onCollapsedChange(!collapsed)}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="w-4 h-4 shrink-0" aria-hidden />
          )}
          Show columns
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400 tabular-nums">
            ({displayedCount} visible
            {enforcedCount > 0 ? ` · ${enforcedCount} enforced` : ''})
          </span>
        </button>
        {!collapsed && showReset ? (
          <button
            type="button"
            onClick={onReset}
            className="text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:underline"
          >
            Reset to default
          </button>
        ) : null}
      </div>

      {!collapsed ? (
        <div className="px-4 pb-3">
          <div ref={listRef} className="flex flex-wrap gap-2">
            {fields.map((field) => {
              const isDragging = dragKey === field.key;
              const isDropTarget = dropKey === field.key && dragKey != null && dragKey !== field.key;
              return (
                <div
                  key={field.key}
                  data-field-key={field.key}
                  onPointerDown={(event) => onChipPointerDown(field.key, event)}
                  className={`${modeChipClasses(field.mode, isDragging)} cursor-grab active:cursor-grabbing touch-none ${
                    isDropTarget ? 'ring-2 ring-offset-2 ring-emerald-600 dark:ring-emerald-300' : ''
                  }`}
                >
                  <span
                    className={`inline-flex items-center justify-center min-w-8 self-stretch px-1.5 rounded-l-full border-r border-black/10 dark:border-white/15 ${modeButtonHover(field.mode)}`}
                    title="Drag to reorder"
                    aria-hidden
                  >
                    <GripVertical className="w-4 h-4" aria-hidden />
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (suppressClickRef.current) {
                        suppressClickRef.current = false;
                        return;
                      }
                      onCycleMode(field.key);
                    }}
                    title={field.title ?? modeTitle(field.mode, field.label)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-r-full cursor-grab ${modeButtonHover(field.mode)}`}
                  >
                    {field.mode === 'require_value' ? (
                      <Filter className="w-3 h-3 shrink-0 opacity-90" aria-hidden />
                    ) : null}
                    {field.label}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
